import React from 'react';
import type { BrokerInfo } from '../../../services/orderLookups.service';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';

interface BrokerInfoModalProps {
  info: BrokerInfo | null;
  onClose: () => void;
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between gap-3">
    <span className="text-muted whitespace-nowrap">{label}</span>
    <span className="text-heading text-right">{value || '-'}</span>
  </div>
);

/**
 * Read-only Broker/Forwarder details popup for the Pro drawer's Addl Info tab —
 * V1 parity. The name serves as the header title (no duplicate Name row), and
 * `additionalinfo` is never rendered (the brokerforwarder table has no such
 * column; it exists on the payload for parity only). Mirrors
 * LocationInfoModal's structure and styling.
 */
const BrokerInfoModal: React.FC<BrokerInfoModalProps> = ({ info, onClose }) => {
  // Read-only broker card: Alt+C closes. Must sit ABOVE the `!info` guard —
  // this modal stays mounted with info=null, so a hook below the guard would
  // change the hook count when info arrives (React #310). `layer: !!info`
  // is what makes it inert while closed.
  useAltHotkeys({ C: onClose }, undefined, { layer: !!info });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(!!info);

  if (!info) return null;

  return (
    <>
      <div className="fixed inset-0 bg-scrim/30 z-50" onClick={onClose} aria-label="Close broker info" />
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div
        role="dialog"
        aria-label={`Broker/Forwarder details for ${info.name}`}
        ref={panelRef}
        style={panelStyle}
        className="pointer-events-auto w-[400px] max-h-[80vh] overflow-y-auto bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
      >
        <ModalHeader
          title={info.name || 'Broker/Forwarder'}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="p-4 text-xs space-y-4">
          <div className="space-y-1">
            <Row label="Record:" value={String(info.recno)} />
            <Row label="Contact:" value={info.contact} />
            <Row label="Address:" value={[info.address, info.address2].filter(Boolean).join(', ')} />
            <Row label="City/State/Zip:" value={[info.city, info.state, info.zip].filter(Boolean).join(', ')} />
            <Row label="Phone:" value={info.phone} />
            <Row label="Fax:" value={info.fax} />
            <Row label="Email:" value={info.email} />
          </div>

          {info.notes && (
            <div>
              <div className="text-muted mb-0.5">Notes:</div>
              <div className="text-heading whitespace-pre-wrap pl-2">
                {info.notes}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default BrokerInfoModal;
